<template>
  <div class="nft-search">
    <div class="search-container">
      <input
        v-model="searchTerm"
        type="text"
        class="search-input"
        placeholder="Search NFTs..."
        @input="debouncedSearch"
        @keyup.enter="handleSearch"
        :disabled="searchInProgress"
      />
      <div v-if="searchInProgress" class="search-loading">
        <span>Searching...</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import type { NFT } from '../types/nft'

// Types
interface SearchOptions {
  loadMore?: () => Promise<void>
  hasMoreNFTs?: boolean
  isLoading?: boolean
  currentPageKey?: string
}

// Props
interface Props {
  nfts: NFT[]
  searchOptions?: SearchOptions
  debugMode?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  nfts: () => [],
  searchOptions: () => ({}),
  debugMode: false
})

// Emits
const emit = defineEmits<{
  searchResults: [nfts: NFT[]]
  searchStart: []
  searchComplete: [message?: string]
}>()

// Reactive state
const searchTerm = ref('')
const searchInProgress = ref(false)
const searchTimeout = ref<NodeJS.Timeout | null>(null)

// Debug function
const debug = (...args: any[]) => {
  if (props.debugMode) {
    console.log('[NFT Search]', ...args)
  }
}



// Methods
const debouncedSearch = () => {
  // Clear any existing timeout
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value)
  }
  
  // Set a new timeout for 300ms
  searchTimeout.value = setTimeout(() => {
    handleSearch()
  }, 300)
}

const handleSearch = async () => {
  if (searchInProgress.value) {
    debug('Search already in progress, ignoring new search term.')
    return
  }

  if (!searchTerm.value || searchTerm.value.trim() === '') {
    debug('Empty search term, showing all NFTs')
    emit('searchResults', props.nfts)
    return
  }

  searchInProgress.value = true
  debug(`Searching for term: "${searchTerm.value}"`)
  emit('searchStart')

  try {
    const term = searchTerm.value.toLowerCase()
    debug(`Searching for term: "${term}" in ${props.nfts.length} NFTs`)

    // Search through currently loaded NFTs
    let foundNFTs = props.nfts.filter(nft => {
      const name = (nft.name || nft.title || '').toLowerCase()
      const collection = (nft.contract?.name || nft.contractMetadata?.name || '').toLowerCase()
      const matches = name.includes(term) || collection.includes(term)
      
      if (props.debugMode && (name.includes(term) || collection.includes(term))) {
        debug(`Found match: "${nft.name || nft.title}" (name: "${name}") or "${nft.contract?.name || nft.contractMetadata?.name}" (collection: "${collection}")`)
      }
      
      return matches
    })

    // If no results found and there are more NFTs to load, keep loading and searching
    while (foundNFTs.length === 0 && props.searchOptions.hasMoreNFTs && !props.searchOptions.isLoading) {
      debug('No results found in current NFTs, loading more...')

      // Store current state before loading
      const currentPageKey = props.searchOptions.currentPageKey
      const currentNFTCount = props.nfts.length

      // Call the load more function if provided
      if (props.searchOptions.loadMore) {
        await props.searchOptions.loadMore()

        // Check if we actually got new NFTs (prevent infinite loop)
        if (props.nfts.length === currentNFTCount || props.searchOptions.currentPageKey === currentPageKey) {
          debug('No new NFTs loaded, breaking search loop')
          break
        }

        // Search again in the expanded NFT list
        foundNFTs = props.nfts.filter(nft => {
          const name = (nft.name || nft.title || '').toLowerCase()
          const collection = (nft.contract?.name || nft.contractMetadata?.name || '').toLowerCase()
          return name.includes(term) || collection.includes(term)
        })
      } else {
        break
      }
    }

    debug(`Search completed. Found ${foundNFTs.length} matching NFTs out of ${props.nfts.length} total NFTs.`)
    emit('searchResults', foundNFTs)

  } catch (error) {
    console.error('Error during search:', error)
    emit('searchComplete', 'Search failed. Please try again.')
  } finally {
    searchInProgress.value = false
    emit('searchComplete')
  }
}

// Watch for search term changes
watch(searchTerm, (newTerm) => {
  debug('Search term changed to:', newTerm)
  if (newTerm === '') {
    // If search is cleared, show all NFTs immediately
    emit('searchResults', props.nfts)
  } else {
    // Use debounced search for non-empty terms
    debouncedSearch()
  }
}, { flush: 'post' })

// Watch for NFTs changes to reset search if needed
watch(() => props.nfts, (newNFTs, oldNFTs) => {
  debug('NFTs changed from', oldNFTs?.length || 0, 'to', newNFTs?.length || 0)
  // Only reset search if we're going from 0 to some NFTs (initial load)
  // Don't interfere with ongoing operations
  if ((oldNFTs?.length || 0) === 0 && newNFTs?.length > 0) {
    debug('Initial NFT load detected, resetting search state')
    searchTerm.value = ''
    searchInProgress.value = false
    // Don't emit search results here - let the parent handle it
  }
}, { deep: true })

// Expose methods for external use
defineExpose({
  resetSearch: () => {
    searchTerm.value = ''
    searchInProgress.value = false
    if (searchTimeout.value) {
      clearTimeout(searchTimeout.value)
      searchTimeout.value = null
    }
    debug('Search state reset')
  },
  isSearchInProgress: () => searchInProgress.value
})
</script>

<style scoped>
.nft-search {
  margin-bottom: 1rem;
}

.search-container {
  position: relative;
  display: flex;
  align-items: center;
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 0.5rem;
  font-size: 1rem;
  background: white;
  transition: border-color 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.search-input:disabled {
  background-color: #f8f9fa;
  cursor: not-allowed;
}

.search-loading {
  position: absolute;
  right: 1rem;
  color: #6c757d;
  font-size: 0.875rem;
}
</style> 